import { isWebPushSupported, webPushPermission } from "@iec-id/shared/utils/web-push-client";
import { useState } from "react";
import { api } from "@/lib/trpc";

/**
 * Почему подписывать нечем. Причина нужна ЭКРАНУ, а не только логике: «браузер
 * не умеет» и «вы уже включили» — разные сообщения человеку, и показывать
 * первое второму значит врать ему в лицо.
 */
export type PushOfferReason =
	/** Есть чем подписывать. */
	| "ready"
	/** Ответ сервера ещё в пути. */
	| "loading"
	/** iOS до 16.4, встроенные вебвью — web-push нет в принципе. */
	| "unsupported"
	/** Разрешение уже выдано: подписка на этом домене есть. */
	| "granted"
	/** Разрешение отклонено — окно браузера больше не появится. */
	| "denied"
	/** VAPID-ключи на сервере не заданы: подписывать нечем с нашей стороны. */
	| "server-off";

export interface PushOffer {
	/**
	 * `skip` — предлагать нечего или незачем; `loading` — ответ сервера ещё в
	 * пути; `ready` — есть чем подписывать.
	 */
	status: "skip" | "loading" | "ready";
	/** Почему именно так — для подписи на экране. */
	reason: PushOfferReason;
	/** Публичный VAPID-ключ. Непустой только при `ready`. */
	publicKey: string;
}

/**
 * Можно ли предложить push на шаге «Уведомления и рассылки».
 *
 * ── ЧЕГО ЗДЕСЬ БОЛЬШЕ НЕТ ──────────────────────────────────────────────────
 *
 * Проверки «мы уже спрашивали в этом браузере» (`push-storage`). Она была
 * уместна, пока предложение висело ЭКРАНОМ ПОСЛЕ финала: спросили один раз и
 * больше не пристаём. С 23.08.2026 это шаг мастера, от которого зависят
 * приветственные Баллы, и та же проверка стала вредной: человек, перезагрузивший
 * страницу посреди мастера, проходил шаги заново и на этом экране получал
 * «браузер не умеет показывать push» — при полностью исправном браузере.
 *
 * Спрашивать дважды в одном мастере нестрашно: разрешение браузер помнит сам, и
 * второй показ увидит только тот, кто в прошлый раз отказался.
 *
 * Пригодность снимается ОДИН раз при монтировании: `Notification.permission`
 * меняется прямо во время работы экрана (человек нажал «Включить»), и живое
 * чтение убрало бы кнопку из-под рук в момент успеха.
 */
export function usePushOffer(): PushOffer {
	const [local] = useState<PushOfferReason>(() => {
		if (!isWebPushSupported()) return "unsupported";
		const permission = webPushPermission();
		if (permission === "granted") return "granted";
		if (permission === "denied") return "denied";
		return "ready";
	});

	// Запрос не уходит вовсе, когда предлагать нечего: лишний поход на сервер
	// в середине мастера задерживает человека без причины.
	const config = api.notifications.pushConfig.useQuery(undefined, {
		enabled: local === "ready",
		retry: false,
		staleTime: 5 * 60_000,
	});

	if (local !== "ready") return { status: "skip", reason: local, publicKey: "" };
	if (config.isError) return { status: "skip", reason: "server-off", publicKey: "" };
	if (config.isPending) return { status: "loading", reason: "loading", publicKey: "" };

	const publicKey = config.data.enabled ? config.data.publicKey : "";
	return publicKey === ""
		? { status: "skip", reason: "server-off", publicKey: "" }
		: { status: "ready", reason: "ready", publicKey };
}
